import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AUDIO_BATCH_MAX_ENHANCEMENT_HINTS } from '@kiao-voice/api-contract/batch-transcription'
import {
  expandHints,
  flattenEnhancementHints,
  normalizeHint,
  type DictionarySource,
  type ExpandedHints,
} from '@kiao-voice/core'
import {
  getHintSuggestions,
  isHintSuggestionsCollapsed,
  recordHintUsage,
  removeHintTerm,
  setHintSuggestionsCollapsed,
  type HintStorageLike,
  type HintSuggestion,
} from '@/features/dictionary/hint-suggestions'

export interface HintTermsOptions {
  /** The modal's open state; suggestions are only read while it is open. */
  open: boolean
  /** Every mutation is inert while an upload is in flight. */
  submitting: boolean
  /** The checked dictionaries' terms, which expand alongside the typed ones. */
  dictionarySources: DictionarySource[]
  /** Test seam for the local frecency store. */
  hintStorage?: HintStorageLike
}

export interface HintTerms {
  /** Hand-typed terms, in the order they were added. */
  hints: string[]
  hintInput: string
  setHintInput: (value: string) => void
  /** Typed and dictionary terms merged, with what was dropped and why. */
  expandedHints: ExpandedHints
  /** The flat term list actually shipped with the upload. */
  visibleHints: string[]
  /** Past the cap — Start is held down until the user trims the list. */
  overHintLimit: boolean
  /** At the cap — further additions are refused rather than silently dropped. */
  hintLimitReached: boolean
  suggestions: HintSuggestion[]
  suggestionsCollapsed: boolean
  addHint: () => void
  addHintTerm: (term: string) => void
  removeHint: (term: string) => void
  excludeHint: (term: string) => void
  restoreHint: (term: string) => void
  toggleSuggestionsCollapsed: () => void
  /**
   * A term now lives in a dictionary, so the suggestion row stops offering it
   * and submit stops recording it back into the local store.
   */
  graduateTerm: (term: string) => void
  /** Feed the frecency store the hand-typed terms that actually shipped. */
  recordShippedUsage: () => void
  /** Fresh state for a new open. */
  reset: () => void
  /**
   * The subset a workspace switch clears. Deliberately NOT `reset()`: the typed
   * terms and the input survive a switch, but the exclusions and the graduated
   * set are scoped to the workspace whose dictionaries produced them — carrying
   * them over would silently tombstone terms in the new workspace's dictionaries
   * and keep already-filed words out of the local frecency store.
   */
  clearForWorkspaceSwitch: () => void
}

/**
 * The vocabulary the user hands this upload: terms typed here, merged with the
 * terms of every checked dictionary.
 *
 * CONSTRAINT: call this unconditionally, above any early return the modal makes
 * for its closed state. It mounts hooks of its own — including one keyed on
 * `open` — so a conditional call changes the hook order between renders.
 *
 * The cap is enforced at ONE entry point (`addHintTerm`), because a word must
 * never be dropped on the floor: suggestion-row clicks land there too, and a
 * store entry written by another build may fail today's rules — pushed raw it
 * would vanish into `manual.skippedCount` with no feedback.
 */
export function useHintTerms({
  open,
  submitting,
  dictionarySources,
  hintStorage,
}: HintTermsOptions): HintTerms {
  const [hints, setHints] = useState<string[]>([])
  const [hintInput, setHintInput] = useState('')
  /** Terms excluded for this upload, across dictionaries and hand-typed hints. */
  const [excludedHintTerms, setExcludedHintTerms] = useState<string[]>([])
  const [suggestions, setSuggestions] = useState<HintSuggestion[]>([])
  const [suggestionsCollapsed, setSuggestionsCollapsed] = useState(false)
  // Terms saved into a dictionary this session — see graduateTerm.
  const graduatedTermsRef = useRef<Set<string>>(new Set())

  const suggestionDeps = useMemo(
    () => (hintStorage ? { storage: hintStorage } : undefined),
    [hintStorage]
  )

  // The collapsed preference is persisted, so it has to be read per open rather
  // than kept in component state across mounts.
  useEffect(() => {
    if (!open) return
    setSuggestionsCollapsed(isHintSuggestionsCollapsed(suggestionDeps))
  }, [open, suggestionDeps])

  const expandedHints = useMemo(
    () => expandHints(dictionarySources, hints, excludedHintTerms),
    [dictionarySources, hints, excludedHintTerms]
  )
  const visibleHints = useMemo(() => flattenEnhancementHints(expandedHints), [expandedHints])
  const overHintLimit = expandedHints.totalCount > AUDIO_BATCH_MAX_ENHANCEMENT_HINTS
  const hintLimitReached = expandedHints.totalCount >= AUDIO_BATCH_MAX_ENHANCEMENT_HINTS

  const refreshSuggestions = useCallback(
    (exclude: string[]) => {
      setSuggestions(getHintSuggestions({ exclude }, suggestionDeps))
    },
    [suggestionDeps]
  )

  useEffect(() => {
    if (!open) return
    refreshSuggestions(visibleHints)
  }, [open, visibleHints, refreshSuggestions])

  // Over the cap the Start button is what stops the upload — a word is never
  // dropped on the floor here (plan §5.2, D7). Normalize at this single entry
  // point: suggestion-row clicks also land here, and a store entry written by
  // another build/tab may fail today's rules.
  const addHintTerm = useCallback(
    (term: string) => {
      if (submitting || hintLimitReached) return
      const normalized = normalizeHint(term)
      if (normalized === null) return
      // Typing an excluded term again means "I want it back" — restore it, the
      // same as clicking its tombstone. Without this the add is a silent no-op.
      setExcludedHintTerms((prev) =>
        prev.includes(normalized) ? prev.filter((t) => t !== normalized) : prev
      )
      setHints((prev) => (prev.includes(normalized) ? prev : [...prev, normalized]))
    },
    [hintLimitReached, submitting]
  )

  const addHint = useCallback(() => {
    if (submitting) return
    const normalized = normalizeHint(hintInput)
    if (!normalized) return
    addHintTerm(normalized)
    setHintInput('')
  }, [addHintTerm, hintInput, submitting])

  const removeHint = useCallback(
    (term: string) => {
      if (submitting) return
      setExcludedHintTerms((prev) => (prev.includes(term) ? prev : [...prev, term]))
    },
    [submitting]
  )

  const excludeHint = useCallback(
    (term: string) => {
      if (submitting) return
      setExcludedHintTerms((prev) => (prev.includes(term) ? prev : [...prev, term]))
    },
    [submitting]
  )

  const restoreHint = useCallback(
    (term: string) => {
      if (submitting) return
      setExcludedHintTerms((prev) => prev.filter((excluded) => excluded !== term))
    },
    [submitting]
  )

  const toggleSuggestionsCollapsed = useCallback(() => {
    const next = !suggestionsCollapsed
    setSuggestionsCollapsed(next)
    setHintSuggestionsCollapsed(next, suggestionDeps)
  }, [suggestionDeps, suggestionsCollapsed])

  /** Graduate a term out of the local frecency store: it lives in a dictionary
   *  now, so the suggestion row should stop offering it (plan §5.3/§5.4). The
   *  ref keeps submit's usage recording from re-inserting a graduated term
   *  whose chip (deliberately) stays in the manual group. */
  const graduateTerm = useCallback(
    (term: string) => {
      graduatedTermsRef.current.add(term)
      removeHintTerm(term, suggestionDeps)
      refreshSuggestions(visibleHints)
    },
    [refreshSuggestions, suggestionDeps, visibleHints]
  )

  const recordShippedUsage = useCallback(() => {
    // Frecency is fed by words that actually shipped, and only by the
    // hand-typed ones — dictionary terms already have a permanent home.
    for (const term of expandedHints.manual.hints) {
      // A term saved into a dictionary this session already graduated out of
      // the frecency store — recording it again would resurrect it there.
      if (graduatedTermsRef.current.has(term)) continue
      recordHintUsage(term, suggestionDeps)
    }
  }, [expandedHints, suggestionDeps])

  const reset = useCallback(() => {
    setHints([])
    setHintInput('')
    setExcludedHintTerms([])
    graduatedTermsRef.current = new Set()
  }, [])

  const clearForWorkspaceSwitch = useCallback(() => {
    setExcludedHintTerms([])
    graduatedTermsRef.current = new Set()
  }, [])

  return {
    hints,
    hintInput,
    setHintInput,
    expandedHints,
    visibleHints,
    overHintLimit,
    hintLimitReached,
    suggestions,
    suggestionsCollapsed,
    addHint,
    addHintTerm,
    removeHint,
    excludeHint,
    restoreHint,
    toggleSuggestionsCollapsed,
    graduateTerm,
    recordShippedUsage,
    reset,
    clearForWorkspaceSwitch,
  }
}
